import React from 'react';
import { Box, Typography, Paper, Avatar, Chip, Card, CardContent, Grid, Button } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArticleIcon from '@mui/icons-material/Article';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useWizard } from '../../context/WizardContext';
import { useParams } from 'react-router-dom';
import { useAdvisor } from '../../hooks/useAdvisor';
import { formatAdvisorName } from '../../utils/formatAdvisorName';
import planScoutLogo from '../../assets/PlanScout_orange_Logo.jpg';
import raygarPhoto from '../../assets/planscout/raygar-khailany.webp';
import iconChecklist from '../../assets/icons/icon-checklist.png';
import iconGraph from '../../assets/icons/icon-graph.png';
import iconAssessment from '../../assets/icons/icon-assessment.png';

interface WelcomeStepProps {
  onNext?: () => void;
}

const WelcomeStep: React.FC<WelcomeStepProps> = ({ onNext }) => {
  const { wizardData } = useWizard();
  const { idCustomer } = useParams<{ idCustomer: string }>();
  const hasCustomerId = !!idCustomer;
  const advisor = useAdvisor();

  // Derive wizard path from intakeVersion + automationEnabled (database-driven)
  // Also check URL ?mode=basic override so dual-mode advisors show the right content
  const urlModeOverride = new URLSearchParams(window.location.search).get('mode');
  const isDemoAdvisor = advisor?.intakeVersion === 'demo';
  const isFullAutomation = !urlModeOverride && advisor?.intakeVersion === 'full' && advisor?.automationEnabled === true;
  const isBasicAutomation = urlModeOverride === 'basic' || urlModeOverride === 'basic-healthspan' || advisor?.intakeVersion === 'basic' || advisor?.intakeVersion === 'basic-healthspan';

  // Use advisor logo if available, otherwise use PlanScout logo
  const logoSrc = advisor?.companyLogoUrl || planScoutLogo;
  const logoAlt = advisor ? `${advisor.company} Logo` : 'PlanScout Logo';

  // Shared advisor card component — de-emphasized with "YOUR ADVISOR" label
  const advisorCard = advisor ? (
    <Box sx={{ width: '100%' }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5, fontSize: '0.7rem', ml: 0.5, mb: 0.5, display: 'block' }}>
        YOUR ADVISOR
      </Typography>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          bgcolor: 'rgba(0, 0, 0, 0.02)',
          borderRadius: 3,
          border: '1px solid rgba(0, 0, 0, 0.06)',
          width: '100%'
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: 2.5
        }}>
          <Avatar
            src={advisor.advisorPhotoUrl}
            alt={advisor.name}
            sx={{
              width: { xs: 130, sm: 156 },
              height: { xs: 130, sm: 156 },
              border: '3px solid #fff',
              boxShadow: 2
            }}
          />
          <Box sx={{
            flex: 1,
            textAlign: { xs: 'center', sm: 'left' },
            minWidth: 0
          }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {formatAdvisorName(advisor.name)}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
              {advisor.company}
            </Typography>
            <Typography variant="body2" sx={{ color: '#2BA784', fontWeight: 500, mb: 1.5 }}>
              Available if you have questions
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <Chip
                icon={<EmailIcon />}
                label={advisor.email}
                variant="outlined"
                size="small"
                sx={{ '& .MuiChip-label': { fontSize: '0.75rem', px: 1 } }}
              />
              {advisor.phone && (
                <Chip
                  icon={<PhoneIcon />}
                  label={advisor.phone}
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiChip-label': { fontSize: '0.75rem', px: 1 } }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  ) : null;

  // 3-step infographic for basic automation
  const stepInfographic = (
    <Box sx={{ width: '100%', maxWidth: 700, mx: 'auto', py: 2 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: 0,
        position: 'relative'
      }}>
        {[
          { icon: <img src={iconChecklist} alt="Checklist" style={{ width: 42, height: 42, objectFit: 'contain' }} />, title: 'Answer a few questions', subtitle: 'Takes about 2 minutes' },
          { icon: <img src={iconGraph} alt="Graph" style={{ width: 42, height: 42, objectFit: 'contain' }} />, title: 'Your responses get analyzed', subtitle: 'To build your personalized financial snapshot' },
          { icon: <img src={iconAssessment} alt="Assessment" style={{ width: 42, height: 42, objectFit: 'contain' }} />, title: 'Get tailored insights', subtitle: 'Review your results with your Advisor' },
        ].map((step, i) => (
          <React.Fragment key={i}>
            {i > 0 && (
              <Box sx={{
                width: { xs: 24, sm: 48 },
                height: 2,
                bgcolor: 'rgba(0, 0, 0, 0.15)',
                mt: { xs: 4.5, sm: 5.5 },
                flexShrink: 0
              }} />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0, px: { xs: 0.5, sm: 1 } }}>
              <Box sx={{
                width: { xs: 72, sm: 90 },
                height: { xs: 72, sm: 90 },
                borderRadius: '50%',
                border: '3px solid rgba(180, 195, 225, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                bgcolor: 'transparent'
              }}>
                {step.icon}
              </Box>
              <Typography fontWeight="bold" textAlign="center" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, lineHeight: 1.3 }}>
                {step.title}
              </Typography>
              <Typography color="text.secondary" textAlign="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, mt: 0.5, lineHeight: 1.4 }}>
                {step.subtitle}
              </Typography>
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      textAlign: 'left',
      maxWidth: 700,
      mx: 'auto'
    }}>
      {hasCustomerId ? (
        isDemoAdvisor ? (
          <>
            {advisorCard}

            {advisor?.features?.branding?.automationWelcomeMessage && (
              <Typography variant="h5" sx={{
                textAlign: 'center',
                fontSize: '1.5rem',
                mb: 2,
                fontWeight: 'bold'
              }}>
                {advisor?.features?.branding?.automationWelcomeMessage}
              </Typography>
            )}

            <Box sx={{ mt: 1 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  width: '100%'
                }}
              >
                <Typography variant="body1" paragraph>
                  Ready to supercharge your planning? This is your sneak peek at PlanScout's delightfully turnkey financial planning service.
                </Typography>

                <Typography variant="body1" paragraph fontWeight="bold">
                  Here's how the magic happens:
                </Typography>

                <Typography variant="body1" paragraph>
                  <strong>1. Smart Intake:</strong> You (or your clients/prospects!) use your custom, branded planning wizard to effortlessly capture key financial details.
                </Typography>

                <Typography variant="body1" paragraph>
                  <strong>2. Instant Notification & Joy!</strong> Get an email ping the moment a plan is submitted via your unique PlanScout URL. We're all about immediate value and a little happy dance!
                </Typography>

                <Typography variant="body1" paragraph>
                  <strong>3. Plan Ready, Pronto:</strong> PlanScout instantly crafts a polished financial plan and presentation just for you. For this demo, we'll email it. Once you're onboarded, it's right there in your planning software!
                </Typography>

                <Typography variant="body1" paragraph>
                  <strong>4. Concierge-Level Support:</strong> Need tweaks? Want our planners to help you nail that presentation? Or just looking for some expert consultative support to win that client? You got it! Our concierge team is here to help you shine.
                </Typography>

                <Typography variant="body1" paragraph fontWeight="bold" sx={{ mt: 3 }}>
                  Let's make planning a breeze!
                </Typography>

                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mt: 3
                }}>
                  <Avatar
                    src={raygarPhoto}
                    alt="Raygar Khailany"
                    sx={{
                      width: 60,
                      height: 60,
                      border: '2px solid #1976d2'
                    }}
                  />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      -Raygar
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      CEO and Co-Founder
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </>
        ) : isFullAutomation ? (
          <>
            {advisorCard}

            <Typography variant="body1" sx={{
              textAlign: 'center',
              fontSize: '1.25rem',
              mb: 1
            }}>
              Welcome to Your Financial Planning Journey
            </Typography>

            <Box sx={{ mt: 1 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  width: '100%'
                }}
              >
                <Typography variant="body1" paragraph fontWeight="bold">
                  We're here to help you create a custom, comprehensive financial plan.
                </Typography>
                <Typography variant="body1" paragraph>
                ✔ <Typography component="span" fontWeight="bold">Take 5 minutes</Typography> to answer a few simple questions, and we'll get started on a custom plan just for you!
                </Typography>
                <Typography variant="body1" paragraph>
                ✔ In just a few clicks, our smart planning wizard gathers your financial details and retirement goals to create a personalized strategy.
                </Typography>
              </Paper>
            </Box>
          </>
        ) : isBasicAutomation ? (
          <>
            {/* Headline */}
            <Typography variant="h4" sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: { xs: '1.5rem', sm: '1.85rem' },
              lineHeight: 1.3,
              mt: 1
            }}>
              {advisor?.features?.branding?.automationWelcomeMessage || 'Get your 2-minute personalized financial snapshot'}
            </Typography>

            {/* Green CTA Button */}
            <Button
              variant="contained"
              size="large"
              onClick={onNext}
              endIcon={<ArrowForwardIcon />}
              sx={{
                bgcolor: '#2BA784',
                color: '#fff',
                width: '100%',
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(43, 167, 132, 0.35)',
                '&:hover': {
                  bgcolor: '#249573',
                  boxShadow: '0 6px 20px rgba(43, 167, 132, 0.45)',
                }
              }}
            >
              Get Started
            </Button>

            {/* Subtitle */}
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: -0.5, mb: 3 }}>
              4 simple questions  ·  No account needed
            </Typography>

            {/* 3-step infographic */}
            {stepInfographic}

            {/* Advisor card — de-emphasized, below */}
            {advisorCard}
          </>
        ) : (
          <>
            {advisorCard}

            <Typography variant="body1" sx={{
              textAlign: 'center',
              fontSize: '1.25rem',
              mb: 1
            }}>
              Welcome to Your Financial Planning Journey
            </Typography>

            <Box sx={{ mt: 1 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  width: '100%'
                }}
              >
                <Typography variant="body1" paragraph fontWeight="bold">
                  We're here to help you create a custom, comprehensive financial plan.
                </Typography>
                <Typography variant="body1" paragraph>
                ✔ <Typography component="span" fontWeight="bold">Take 5 minutes</Typography> to answer a few simple questions, and we'll get started on a custom plan just for you!
                </Typography>
                <Typography variant="body1" paragraph>
                ✔ In just a few clicks, our smart planning wizard gathers your financial details and retirement goals to create a personalized strategy.
                </Typography>
              </Paper>
            </Box>
          </>
        )
      ) : (
        <>
          {advisorCard}

          <Paper
            elevation={3}
            sx={{
              p: 4,
              bgcolor: 'rgba(25, 118, 210, 0.08)',
              color: 'text.primary',
              width: '120%',
              maxWidth: 800,
              mx: -4,
              position: 'relative',
              mb: 4
            }}
          >
            <Typography variant="body1" paragraph sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
              ✅  Try Our 5-Minute Client Prospecting Demo!
            </Typography>
            <Typography variant="body1" paragraph>
              Want a personalized financial plan?
            </Typography>
            <Typography variant="body1" paragraph>
              <Typography component="span" fontWeight="bold">Take 5 minutes</Typography> to answer a few simple questions, and we'll get started on a custom plan just for you!
            </Typography>
          </Paper>

          <Typography variant="body1" sx={{
            textAlign: 'center',
            fontSize: '1.25rem',
            mb: 1
          }}>
            Welcome to Your Financial Planning Journey
          </Typography>

          <Box sx={{ mt: -2 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                bgcolor: 'background.default',
                borderRadius: 2,
                width: '100%'
              }}
            >
              <Typography variant="body1" paragraph fontWeight="bold">
                We're here to help you create a custom, comprehensive financial plan.
              </Typography>
              <Typography variant="body1" paragraph>
              ✔ In just a few clicks, our smart planning wizard gathers you or your clients' financial details and retirement goals to create a personalized strategy.
              </Typography>
              <Typography variant="body1" paragraph>
              ✔ You can deliver a plan—branded with your logo—as a powerful first step in your planning process, to engage prospects and help them take action on their retirement future.
              </Typography>
            </Paper>
          </Box>
        </>
      )}

      {/* Useful Links Section */}
      {advisor && advisor?.features?.branding?.usefulLinks && advisor?.features?.branding?.usefulLinks.length > 0 && (
        <Box sx={{ mt: 3, mb: 2 }}>
          <Grid container spacing={2}>
            {advisor?.features?.branding?.usefulLinks.map((link, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3,
                      '& .MuiCardContent-root': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                      }
                    }
                  }}
                  onClick={() => window.open(link.url, '_blank')}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      {link.icon === 'calendar' && <CalendarTodayIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: '1.4rem' }} />}
                      {link.icon === 'article' && <ArticleIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: '1.4rem' }} />}
                      {link.icon === 'media' && <NewspaperIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: '1.4rem' }} />}
                      {(!link.icon || link.icon === 'external') && <OpenInNewIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: '1.4rem' }} />}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '0.95rem', mb: 0.5 }}>
                          {link.title}
                        </Typography>
                        {link.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                            {link.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Subtle "Powered by PlanScout" footer */}
      {advisor?.features?.branding?.showPoweredByPlanScout !== false && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mt: 3,
          opacity: 0.6,
          '&:hover': {
            opacity: 0.8
          }
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Powered by
          </Typography>
          <Box
            component="img"
            src={planScoutLogo}
            alt="PlanScout"
            sx={{
              height: 30,
              width: 'auto'
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default WelcomeStep;
